import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  fromEvent,
  Observable,
  startWith,
} from 'rxjs';
import { Booking, BookingStatus } from '../../../core/models/entities/booking';
import { BookingType } from '../../../core/enums/booking-type';
import { fromStore } from '../../../core/store';
import { bookingsActions } from '../../../core/store/bookings';
import { ModalService } from '../../../core/services/modal.service';
import { BookingDetailsModalComponent } from '../booking-details-modal/booking-details-modal.component';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { map, take } from 'rxjs/operators';
import { selectAuthVendor } from '../../../core/store/reducers';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { getEditableLocales } from '../../../core/services/locale.service';

@Component({
  selector: 'app-pending-bookings',
  template: `
    @if ((bookings$ | async)?.length > 0) {
      <div>
        <div class="mb-6 flex items-center">
          <div class="mr-4 text-xl font-bold">{{ 'bookings.status.PENDING' | transloco }}</div>
          @if (loading$ | async) {
            <app-loader size="sm"></app-loader>
          } @else {
            <div
              class="flex h-6 w-6 items-center justify-center rounded-full bg-pink-600 text-sm text-white"
            >
              <div>{{ bookingsCount$ | async }}</div>
            </div>
          }
        </div>
        @if ((bookings$ | async)?.length > 0) {
          <div class="mb-4 flex gap-8" [class.flex-wrap]="seeAll$ | async">
            @for (booking of visibleBookings$ | async; track booking) {
              <div
                class="w-full flex-none"
                [style.max-width]="cardMaxWidth + 'px'"
              >
                <div class="relative flex h-full flex-col">
                  <app-card #pendingBooking data-cy="pendingBooking">
                    <div class="flex justify-between">
                      <div>
                        <div class="mb-1 font-semibold">{{
                          booking.company
                            ? booking.company + ' - ' + booking.full_name
                            : booking.full_name
                        }}</div>
                        <div class="mb-1 text-sm text-gray-500">{{
                          booking.room.name
                        }}</div>
                      </div>
                      <div class="flex shrink-0 items-start gap-1">
                        <!-- Langue de la demande : c'est dans celle-ci qu'il
                             faudra répondre au client. -->
                        @if (showLocaleBadge() && booking.locale) {
                          <app-badge
                            variant="info"
                            [tooltip]="'bookings.locale-badge-tooltip' | transloco: { locale: ('settings.vendor.locale.name-' + booking.locale | transloco) }"
                            >{{ booking.locale | uppercase }}</app-badge
                          >
                        }
                        @if (vendor().slug === 'urbagestion') {
                          <app-badge variant="secondary">{{
                            booking.created_by_app ? 'App' : 'Web'
                          }}</app-badge>
                        }
                      </div>
                    </div>
                    @for (room of booking.complementary_rooms; track room) {
                      <div class="mb-1 text-sm text-gray-500">+ {{ room.name }}</div>
                    }
                    <div class="mb-4 text-sm">
                      <app-booking-start-end
                        [booking]="booking"
                      ></app-booking-start-end>
                      @if (booking.booking_type === BookingType.MultipleDates && booking.siblings && booking.siblings.length > 1) {
                        <button
                          type="button"
                          class="mt-1 text-sm text-gray-400 hover:text-gray-600"
                          (click)="openBookingDetails(booking)"
                        >
                          + {{ booking.siblings.length - 1 }} date{{ booking.siblings.length > 2 ? 's' : '' }}
                        </button>
                      }
                    </div>
                    <div class="mt-auto flex justify-end gap-4">
                      <app-button
                        [attr.data-cy]="'open-booking-details'"
                        variant="primary"
                        size="sm"
                        (click)="openBookingDetails(booking)"
                        >{{ 'bookings.pending.open-details' | transloco }}
                      </app-button>
                    </div>
                    <div
                      class="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-pink-600"
                    ></div>
                  </app-card>
                </div>
              </div>
            }
          </div>
        }
        @if (seeAllButtonVisible$ | async) {
          <button (click)="toggle()" class="pt-4 underline">{{
            (seeAll$ | async) === true ? ('common.table.show-less' | transloco) : ('bookings.pending.show-all' | transloco)
          }}</button>
        }
        <app-spacer size="8"></app-spacer>
      </div>
    }
  `,
  standalone: false,
})
export class PendingBookingsComponent
  extends DestroyableComponent
  implements OnInit, AfterViewInit
{
  @ViewChildren('pendingBooking') pendingBooking: QueryList<ElementRef>;
  bookings$: Observable<Booking[]> = this.store.select(
    fromStore.selectAllPendingBookings,
  );
  visibleBookings$: Observable<Booking[]>;
  loading$: Observable<boolean> = this.store.select(
    fromStore.selectAllPendingBookingsLoading,
  );
  bookingsCount$: Observable<number> = this.store.select(
    fromStore.selectAllPendingBookingsCount,
  );
  calendarWidth$: Observable<number>;

  BookingStatus = BookingStatus;
  BookingType = BookingType;
  hasHiddenBookings: boolean;

  seeAll$ = new BehaviorSubject(false);
  hasHiddenBookings$: Observable<boolean>;
  seeAllButtonVisible$: Observable<boolean>;
  vendor = this.store.selectSignal(selectAuthVendor);

  /**
   * Inutile de signaler la langue d'une demande quand l'organisation n'en
   * propose qu'une : toutes les demandes arrivent forcément dans celle-là.
   */
  showLocaleBadge = computed(() => getEditableLocales(this.vendor()).length > 1);

  cardMaxWidth = 352;
  platformId = inject(PLATFORM_ID);
  private translocoService = inject(TranslocoService);

  constructor(
    private store: Store,
    private modalService: ModalService,
    private elem: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    this.store.dispatch(bookingsActions.findAllPending());
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.calendarWidth$ = combineLatest([
        this.bookings$,
        fromEvent(window, 'resize'),
      ]).pipe(
        startWith(this.elem.nativeElement.offsetWidth ?? 0),
        map((ev) => {
          return this.elem.nativeElement.offsetWidth ?? 0;
        }),
      );

      this.visibleBookings$ = combineLatest([
        this.calendarWidth$,
        this.bookings$,
        this.seeAll$,
      ]).pipe(
        map(([width, bookings, seeAll]) => {
          if (seeAll) return bookings;
          return bookings.slice(
            0,
            Math.max(1, Math.trunc(width / (this.cardMaxWidth + 32))),
          );
        }),
      );

      this.hasHiddenBookings$ = combineLatest([
        this.bookings$,
        this.visibleBookings$,
      ]).pipe(
        map(
          ([bookings, visibleBookings]) =>
            bookings?.length > visibleBookings?.length,
        ),
      );

      this.seeAllButtonVisible$ = combineLatest([
        this.hasHiddenBookings$,
        this.seeAll$,
      ]).pipe(
        map(([hasHiddenBookings, seeAll]) => {
          return seeAll || (!seeAll && hasHiddenBookings);
        }),
      );

      this.cdr.detectChanges();
    }
  }

  openBookingDetails(booking: Booking) {
    const instance = this.modalService.open(BookingDetailsModalComponent, {});
    (instance.componentRef.instance as BookingDetailsModalComponent).bookingId =
      booking.id;
  }

  toggle() {
    this.seeAll$
      .pipe(take(1))
      .subscribe((seeAll) => this.seeAll$.next(!seeAll));
  }
}
